/**
 * Attribute-level URL safety for links and images rendered from Markdown.
 *
 * A scheme check on the raw string can be bypassed: `jav&#x61;script:`,
 * `javascript&#58;` (HTML character references), `java\tscript:` (browsers
 * drop tabs and newlines inside URLs), leading control characters, mixed case,
 * protocol-relative `//host` and `/\host`. So the value is first decoded and
 * stripped of what a browser would ignore, then judged by an allowlist:
 *
 *   - `#fragment` and site-relative paths (`/docs/x`, `docs/x`, `?q=`);
 *   - absolute `http:`, `https:` and `mailto:` URLs.
 *
 * Everything else (javascript:, data:, vbscript:, file:, protocol-relative…)
 * becomes `#` for links and is dropped for images.
 */

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  colon: ':',
  sol: '/',
  bsol: '\\',
  tab: '\t',
  newline: '\n',
  nbsp: '\u00a0',
  lpar: '(',
  rpar: ')',
  period: '.',
};

/** Decodes numeric and the common named character references, repeatedly (double-encoding included). */
export function decodeCharacterReferences(value: string): string {
  let current = value;
  for (let round = 0; round < 3; round += 1) {
    const next = current.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);?/gi, (match, body: string) => {
      if (body[0] === '#') {
        const code =
          body[1] === 'x' || body[1] === 'X' ? Number.parseInt(body.slice(2), 16) : Number.parseInt(body.slice(1), 10);
        return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : '';
      }
      return NAMED_ENTITIES[body.toLowerCase()] ?? match;
    });
    if (next === current) break;
    current = next;
  }
  return current;
}

// C0 controls and space, DEL and C1 controls, no-break space, zero-width characters, line/paragraph
// separators and the byte-order mark: browsers drop or ignore these inside URL schemes.
const STRIPPED_CHARACTERS = new RegExp(
  '[\\u0000-\\u0020\\u007f-\\u009f\\u00a0\\u200b-\\u200d\\u2028\\u2029\\ufeff]',
  'g',
);

/** What a browser would act on: references decoded, control characters and whitespace removed. */
export function normalizeUrlValue(value: string): string {
  return decodeCharacterReferences(value).replace(STRIPPED_CHARACTERS, '');
}

const ALLOWED_SCHEMES = new Set(['http:', 'https:', 'mailto:']);

/** A safe href, or null when the value must not be used. */
export function safeHref(value: string): string | null {
  const url = normalizeUrlValue(value);
  if (url === '') return null;
  // Browsers read `\` as `/` in web URLs (`/\host`, `\\host` are protocol-relative); content never needs one.
  if (url.includes('\\')) return null;
  if (url.startsWith('#') || url.startsWith('?')) return url;
  // Protocol-relative (`//host`) leaves the site.
  if (url.startsWith('//')) return null;
  if (url.startsWith('/')) return url;
  // A scheme is anything before the first ':' that comes before any / ? #.
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(url);
  if (!scheme) {
    // No scheme: a relative path. A colon before the first / ? # would be read as one.
    return /^[^/?#]*:/.test(url) ? null : url;
  }
  try {
    const parsed = new URL(url);
    return ALLOWED_SCHEMES.has(parsed.protocol) ? parsed.href : null;
  } catch {
    return null;
  }
}

/**
 * A safe image source: only this site's own /images/ files, returned in the
 * canonical form a browser would request.
 *
 * The check is made on the URL as a browser resolves it, not on the raw
 * string: `/images/%2e%2e/auth` contains no literal `..`, but browsers read
 * `%2e` as a dot segment and request `/auth`. Percent-encoded dots, slashes and
 * backslashes are refused outright (no real image name needs them), and the
 * resolved path must still be under /images/.
 */
export function safeImageSrc(value: string): string | null {
  const url = normalizeUrlValue(value);
  if (!url.startsWith('/images/') || url.includes('\\') || url.includes('//')) return null;
  if (/%(?:2e|2f|5c)/i.test(url)) return null;
  let resolved: URL;
  try {
    resolved = new URL(url, 'https://site.invalid');
  } catch {
    return null;
  }
  if (resolved.origin !== 'https://site.invalid' || !resolved.pathname.startsWith('/images/')) return null;
  const canonical = `${resolved.pathname}${resolved.search}${resolved.hash}`;
  return canonical.split('/').includes('..') ? null : canonical;
}
